import {
  ArrowLeft,
  LoaderCircle,
  Save,
} from 'lucide-react'
import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import ClienteModal from '../clientes/ClienteModal'
import { useEmpresa } from '../../context/EmpresaContext'
import { useModalMensaje } from '../../hooks/useModalMensaje'
import { supabase } from '../../lib/supabase'
import { formatearRut } from '../../utils/rut'
import { TIPOS_DOCUMENTO_INGRESO } from '../../utils/ingresos'
import ModalMensaje from '../ui/ModalMensaje'

const OPCION_CREAR_CLIENTE = '__crear_cliente__'

function obtenerFechaActual() {
  const fecha = new Date()
  const año = fecha.getFullYear()
  const mes = String(fecha.getMonth() + 1).padStart(2, '0')
  const dia = String(fecha.getDate()).padStart(2, '0')

  return `${año}-${mes}-${dia}`
}

function convertirNumero(valor) {
  const numero = Number.parseFloat(valor)

  return Number.isFinite(numero) ? numero : 0
}

function formatearMonto(valor) {
  return convertirNumero(valor).toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  })
}

function obtenerValoresIniciales(ingresoInicial) {
  return {
    fecha_ingreso:
      ingresoInicial?.fecha_ingreso || obtenerFechaActual(),

    fecha_vencimiento:
      ingresoInicial?.fecha_vencimiento || '',

    fecha_pago:
      ingresoInicial?.fecha_pago || '',

    cliente_id:
      ingresoInicial?.cliente_id || '',

    tipo_documento:
      ingresoInicial?.tipo_documento || 'factura',

    numero_documento:
      ingresoInicial?.numero_documento || '',

    descripcion:
      ingresoInicial?.descripcion || '',

    monto_neto:
      ingresoInicial?.monto_neto?.toString() || '',

    monto_iva:
      ingresoInicial?.monto_iva?.toString() || '',

    monto_exento:
      ingresoInicial?.monto_exento?.toString() || '',

    otros_impuestos:
      ingresoInicial?.otros_impuestos?.toString() || '',

    medio_pago:
      ingresoInicial?.medio_pago || '',

    estado_pago:
      ingresoInicial?.estado_pago || 'pendiente',

    observaciones:
      ingresoInicial?.observaciones || '',
  }
}

function FormularioIngreso({
  modo = 'crear',
  ingresoInicial = null,
  cargandoInicial = false,
  onGuardar,
  onCancelar,
}) {
  const { empresaActual } = useEmpresa()
  const {
    modal: modalFormulario,
    abrirModal: abrirModalFormulario,
  } = useModalMensaje()

  const esEdicion = modo === 'editar'

  const [clientes, setClientes] = useState([])
  const [cargandoOpciones, setCargandoOpciones] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [modalClienteAbierto, setModalClienteAbierto] =
    useState(false)

  const [formulario, setFormulario] = useState(() =>
    obtenerValoresIniciales(ingresoInicial)
  )

  const idCargadoRef = useRef(ingresoInicial?.id || null)

  useEffect(() => {
    if (!ingresoInicial) return
    if (idCargadoRef.current === ingresoInicial.id) return

    idCargadoRef.current = ingresoInicial.id
    setFormulario(obtenerValoresIniciales(ingresoInicial))
  }, [ingresoInicial])

  const cargarClientes = async () => {
    if (!empresaActual?.id) return []

    const { data, error: errorClientes } = await supabase
      .from('clientes')
      .select('id, razon_social, rut')
      .eq('empresa_id', empresaActual.id)
      .eq('activo', true)
      .order('razon_social', { ascending: true })

    if (errorClientes) {
      throw errorClientes
    }

    const listado = data || []
    setClientes(listado)

    return listado
  }

  useEffect(() => {
    const cargarOpciones = async () => {
      if (!empresaActual?.id) {
        return
      }

      try {
        setCargandoOpciones(true)
        await cargarClientes()
      } catch (errorCarga) {
        console.error('Error al cargar datos:', errorCarga)

        abrirModalFormulario({
          tipo: 'error',
          titulo: 'Error al cargar datos',
          mensaje:
            errorCarga.message ||
            'No fue posible cargar los clientes.',
        })
      } finally {
        setCargandoOpciones(false)
      }
    }

    cargarOpciones()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaActual?.id])

  const montoTotal = useMemo(() => {
    return (
      convertirNumero(formulario.monto_neto) +
      convertirNumero(formulario.monto_iva) +
      convertirNumero(formulario.monto_exento) +
      convertirNumero(formulario.otros_impuestos)
    )
  }, [
    formulario.monto_neto,
    formulario.monto_iva,
    formulario.monto_exento,
    formulario.otros_impuestos,
  ])

  const actualizarCampo = (event) => {
    const { name, value } = event.target

    setFormulario((formularioActual) => ({
      ...formularioActual,
      [name]: value,
    }))
  }

  const cambiarCliente = (event) => {
    const { value } = event.target

    if (value === OPCION_CREAR_CLIENTE) {
      setModalClienteAbierto(true)
      return
    }

    setFormulario((formularioActual) => ({
      ...formularioActual,
      cliente_id: value,
    }))
  }

  const cerrarModalCliente = () => {
    setModalClienteAbierto(false)
  }

  const alGuardarCliente = async (clienteGuardado) => {
    setModalClienteAbierto(false)

    try {
      await cargarClientes()
    } catch (errorRecarga) {
      console.error(
        'Error al recargar clientes:',
        errorRecarga
      )
    }

    if (clienteGuardado?.id) {
      setFormulario((formularioActual) => ({
        ...formularioActual,
        cliente_id: clienteGuardado.id,
      }))
    }
  }

  const manejarEnvio = async (event) => {
    event.preventDefault()

    if (!formulario.descripcion.trim()) {
      abrirModalFormulario({
        tipo: 'error',
        titulo: 'Descripción requerida',
        mensaje: 'Debes ingresar una descripción.',
      })
      return
    }

    if (montoTotal <= 0) {
      abrirModalFormulario({
        tipo: 'error',
        titulo: 'Monto inválido',
        mensaje: 'El monto total debe ser mayor que cero.',
      })
      return
    }

    const datosNormalizados = {
      cliente_id:
        formulario.cliente_id || null,

      fecha_ingreso:
        formulario.fecha_ingreso,

      fecha_vencimiento:
        formulario.fecha_vencimiento || null,

      fecha_pago:
        formulario.fecha_pago || null,

      tipo_documento:
        formulario.tipo_documento,

      numero_documento:
        formulario.numero_documento.trim() || null,

      descripcion:
        formulario.descripcion.trim(),

      monto_neto:
        convertirNumero(formulario.monto_neto),

      monto_iva:
        convertirNumero(formulario.monto_iva),

      monto_exento:
        convertirNumero(formulario.monto_exento),

      otros_impuestos:
        convertirNumero(formulario.otros_impuestos),

      monto_total:
        montoTotal,

      medio_pago:
        formulario.medio_pago || null,

      estado_pago:
        formulario.estado_pago,

      observaciones:
        formulario.observaciones.trim() || null,
    }

    try {
      setGuardando(true)
      await onGuardar(datosNormalizados)
    } catch (errorGuardado) {
      console.error('Error al guardar ingreso:', errorGuardado)

      abrirModalFormulario({
        tipo: 'error',
        titulo: 'No se pudo guardar',
        mensaje:
          errorGuardado.message ||
          'No fue posible guardar el ingreso.',
      })
    } finally {
      setGuardando(false)
    }
  }

  if (cargandoInicial || cargandoOpciones) {
    return (
      <div className="flex min-h-96 items-center justify-center">
        <div className="text-center">
          <LoaderCircle
            size={38}
            className="mx-auto animate-spin text-emerald-600"
          />

          <p className="mt-4 font-medium text-slate-500">
            Cargando formulario...
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={onCancelar}
        className="mb-5 flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-emerald-700"
      >
        <ArrowLeft size={18} />
        Volver a ingresos
      </button>

      <div className="mb-8">
        <p className="text-sm font-semibold uppercase tracking-wide text-emerald-600">
          {empresaActual?.nombre_fantasia}
        </p>

        <h2 className="mt-2 text-2xl font-bold text-slate-900 sm:text-3xl">
          {esEdicion ? 'Editar ingreso' : 'Nuevo ingreso'}
        </h2>

        <p className="mt-2 text-slate-500">
          {esEdicion && ingresoInicial?.numero_documento
            ? `Documento N.º ${ingresoInicial.numero_documento}`
            : esEdicion
              ? 'Actualiza los antecedentes del ingreso.'
              : 'Ingresa los antecedentes del ingreso.'}
        </p>
      </div>

      <form
        onSubmit={manejarEnvio}
        className="w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          <Campo label="Fecha del ingreso" requerido>
            <input
              type="date"
              name="fecha_ingreso"
              value={formulario.fecha_ingreso}
              onChange={actualizarCampo}
              required
              className={estiloInput}
            />
          </Campo>

          <Campo label="Cliente">
            <select
              name="cliente_id"
              value={formulario.cliente_id}
              onChange={cambiarCliente}
              className={estiloInput}
            >
              <option value="">Sin cliente</option>

              {clientes.map((cliente) => (
                <option
                  key={cliente.id}
                  value={cliente.id}
                >
                  {cliente.razon_social}
                  {cliente.rut
                    ? ` — ${formatearRut(cliente.rut)}`
                    : ''}
                </option>
              ))}

              <option value={OPCION_CREAR_CLIENTE}>
                + Crear nuevo cliente
              </option>
            </select>
          </Campo>

          <Campo label="Tipo de documento">
            <select
              name="tipo_documento"
              value={formulario.tipo_documento}
              onChange={actualizarCampo}
              className={estiloInput}
            >
              {Object.entries(TIPOS_DOCUMENTO_INGRESO).map(
                ([valor, etiqueta]) => (
                  <option key={valor} value={valor}>
                    {etiqueta}
                  </option>
                )
              )}
            </select>
          </Campo>

          <Campo label="Número de documento">
            <input
              type="text"
              name="numero_documento"
              value={formulario.numero_documento}
              onChange={actualizarCampo}
              placeholder="Ejemplo: 1258"
              className={estiloInput}
            />
          </Campo>

          <Campo label="Fecha de vencimiento">
            <input
              type="date"
              name="fecha_vencimiento"
              value={formulario.fecha_vencimiento}
              onChange={actualizarCampo}
              className={estiloInput}
            />
          </Campo>

          <div className="md:col-span-2 xl:col-span-3">
            <Campo label="Descripción" requerido>
              <input
                type="text"
                name="descripcion"
                value={formulario.descripcion}
                onChange={actualizarCampo}
                placeholder="Describe el ingreso realizado"
                required
                className={estiloInput}
              />
            </Campo>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-slate-500">
            Montos
          </h3>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-5">
            <Campo label="Monto neto">
              <input
                type="number"
                name="monto_neto"
                value={formulario.monto_neto}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="IVA">
              <input
                type="number"
                name="monto_iva"
                value={formulario.monto_iva}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="Monto exento">
              <input
                type="number"
                name="monto_exento"
                value={formulario.monto_exento}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="Otros impuestos">
              <input
                type="number"
                name="otros_impuestos"
                value={formulario.otros_impuestos}
                onChange={actualizarCampo}
                min="0"
                step="0.01"
                placeholder="0"
                className={estiloInput}
              />
            </Campo>

            <Campo label="Monto total">
              <input
                type="text"
                value={formatearMonto(montoTotal)}
                readOnly
                className={`${estiloInput} cursor-not-allowed bg-slate-100 font-bold text-slate-900`}
              />
            </Campo>
          </div>
        </div>

        <div className="mt-8 border-t border-slate-200 pt-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            <Campo label="Estado del pago">
              <select
                name="estado_pago"
                value={formulario.estado_pago}
                onChange={actualizarCampo}
                className={estiloInput}
              >
                <option value="pendiente">Pendiente</option>
                <option value="parcial">Pago parcial</option>
                <option value="pagado">Pagado</option>
              </select>
            </Campo>

            <Campo label="Medio de pago">
              <select
                name="medio_pago"
                value={formulario.medio_pago}
                onChange={actualizarCampo}
                className={estiloInput}
              >
                <option value="">Sin especificar</option>
                <option value="efectivo">Efectivo</option>
                <option value="transferencia">
                  Transferencia
                </option>
                <option value="tarjeta_credito">
                  Tarjeta de crédito
                </option>
                <option value="tarjeta_debito">
                  Tarjeta de débito
                </option>
                <option value="cheque">Cheque</option>
                <option value="otro">Otro</option>
              </select>
            </Campo>

            <Campo label="Fecha de pago">
              <input
                type="date"
                name="fecha_pago"
                value={formulario.fecha_pago}
                onChange={actualizarCampo}
                className={estiloInput}
              />
            </Campo>

            <div className="md:col-span-2 xl:col-span-3">
              <Campo label="Observaciones">
                <textarea
                  name="observaciones"
                  value={formulario.observaciones}
                  onChange={actualizarCampo}
                  rows={4}
                  placeholder="Información adicional del ingreso"
                  className={estiloInput}
                />
              </Campo>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col gap-4 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-slate-500">
              Total del ingreso
            </p>

            <p className="text-3xl font-bold text-slate-900">
              {formatearMonto(montoTotal)}
            </p>
          </div>

          <div className="flex flex-col-reverse gap-3 sm:flex-row">
            <button
              type="button"
              onClick={onCancelar}
              disabled={guardando}
              className="w-full rounded-lg border border-slate-300 px-5 py-3 font-semibold text-slate-700 hover:bg-slate-100 sm:w-auto"
            >
              Cancelar
            </button>

            <button
              type="submit"
              disabled={guardando}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 py-3 font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-400 sm:w-auto"
            >
              {guardando ? (
                <LoaderCircle
                  size={20}
                  className="animate-spin"
                />
              ) : (
                <Save size={20} />
              )}

              {guardando
                ? 'Guardando...'
                : esEdicion
                  ? 'Guardar cambios'
                  : 'Guardar ingreso'}
            </button>
          </div>
        </div>
      </form>

      {modalClienteAbierto && (
        <ClienteModal
          cliente={null}
          onClose={cerrarModalCliente}
          onGuardado={alGuardarCliente}
        />
      )}

      <ModalMensaje {...modalFormulario} />
    </div>
  )
}

const estiloInput =
  'w-full rounded-lg border border-slate-300 bg-white px-3 py-3 outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100'

function Campo({ label, requerido = false, children }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}

        {requerido && (
          <span className="ml-1 text-red-500">*</span>
        )}
      </label>

      {children}
    </div>
  )
}

export default FormularioIngreso
